import axios from 'axios';
import { useEffect, useRef } from 'react';
import { router } from '@inertiajs/react';

let lastTrackedTime = 0;
const THROTTLE_TIME = 30000; // 30 seconds
const MAX_RETRIES = 3;

const useTrackVisit = () => {
    const isProcessing = useRef(false);
    const lastTrackedPath = useRef<string | null>(null);

    const getIpDetails = async (retryCount = 0): Promise<any> => {
        try {
            const response = await axios.get('https://ipapi.co/json/', {
                timeout: 5000,
                headers: { 'Accept': 'application/json' }
            });

            // Validate response data
            if (!response.data || typeof response.data !== 'object') {
                throw new Error('Invalid IP API response format');
            }

            return {
                country_name: response.data.country_name || null,
                region_name: response.data.region || null,
                city: response.data.city || null,
                latitude: response.data.latitude || null,
                longitude: response.data.longitude || null,
                timezone: { name: response.data.timezone || null },
                connection: { isp: response.data.org || null }
            };
        } catch (error) {
            if (retryCount < MAX_RETRIES) {
                await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
                return getIpDetails(retryCount + 1);
            }
            console.error('Failed to fetch IP details:', error);
            return null;
        }
    };

    const trackVisit = async (path: string) => {
        const now = Date.now();
        if (now - lastTrackedTime < THROTTLE_TIME) return;
        if (isProcessing.current || lastTrackedPath.current === path) return;

        try {
            isProcessing.current = true;
            const cleanPath = path.split('?')[0].split('#')[0];
            const ipDetails = await getIpDetails();

            const response = await axios.post('/track-visit', {
                currentPath: cleanPath,
                ipDetails
            }, {
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                }
            });

            if (response.data?.status !== 'success' && response.data?.status !== 'skipped') {
                throw new Error(response.data?.message || 'Failed to track visit');
            }

            lastTrackedPath.current = cleanPath;
            lastTrackedTime = Date.now();
        } catch (error: any) {
            console.error('Error tracking visit:', error?.response?.data || error);
        } finally {
            isProcessing.current = false;
        }
    };

    useEffect(() => {
        trackVisit(window.location.pathname);

        const unsubscribe = router.on('finish', (event: any) => {
            if (event.detail.visit.completed) {
                trackVisit(window.location.pathname);
            }
        });

        return () => {
            unsubscribe();
        };
    }, []);
};

export default useTrackVisit;
